import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { Button, DatePicker, Form, Input, message, Select } from 'antd';
import { useCallback, useState, useEffect, useRef } from 'react';
import { setKycResult, setVirifiedStatus } from '@/store/features/plaid/identitySlice';
import { getVerificationResult, getVerificationStatus, updateCustomerProfile } from '@/api/custom';
import { loginPlaid, normalizeDateOfBirth } from '@/pages/kyc/link';
import { setUser } from '@/store/features/userinfo/userinfoSlice';
import { updateUserInfo } from '@/store/features/apex/apexSlice';
import { selectApexUserInfo } from '@/store/features/apex/apex-selectors';
import { setItemWithExpiry } from '@/util/util';
import { mapVerificationResultToKycResult } from '@/pages/kyc/verification-result';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import './plaid-kyc.scss';
import OpenAccountShell from '@/pages/wealth/components/open-brokerage-account/OpenAccountShell';
import {
  type Address,
  type NameSuffixType,
  US_STATES,
  normalizeUSPhoneNumber,
  validateAddress,
  validateDateOfBirth,
  validateName,
  validateMiddleNames,
  validateUSPhoneNumber,
} from '@/util/account-openning-util';

const { Option } = Select;

dayjs.extend(customParseFormat);

const ACCOUNT_OPENING_NEXT_PATH = '/wealth/open-brokerage-account/step1';
const DATE_OF_BIRTH_PICKER_FORMATS = [
  'M/D/YYYY',
  'MM/DD/YYYY',
  'M-D-YYYY',
  'MM-DD-YYYY',
  'YYYY-MM-DD',
];
const DATE_OF_BIRTH_COMMIT_FORMATS = [
  ...DATE_OF_BIRTH_PICKER_FORMATS,
  'M/D/YY',
  'MM/DD/YY',
  'M-D-YY',
  'MM-DD-YY',
];
const AUTHENTICATED_USER_CACHE_TTL_MS = 8 * 60 * 60 * 1000;
const DEFAULT_DATE_OF_BIRTH_PICKER_VALUE = dayjs().subtract(30, 'year');
const SUFFIX_OPTIONS: Array<{ value: NameSuffixType; label: string }> = [
  { value: 'NAME_SUFFIX_UNSPECIFIED', label: 'No suffix' },
  { value: 'SR', label: 'Sr.' },
  { value: 'JR', label: 'Jr.' },
  { value: 'III', label: 'III' },
  { value: 'IV', label: 'IV' },
  { value: 'V', label: 'V' },
];

type PersonalInfoFormValues = {
  firstName: string;
  middleName?: string;
  lastName: string;
  suffix: NameSuffixType;
  phoneNumber: string;
  dateOfBirth: Dayjs;
  personalAddress: Address;
};

type KycPersonalInfo = {
  first_name: string;
  middle_name?: string;
  last_name: string;
  email: string;
  date_of_birth: string;
  phone_number: string;
  street: string;
  city: string;
  state: string;
  zip_code: string;
  suffix: NameSuffixType;
  personalAddress: Address;
  dateOfBirthForStore: Date;
};

type LoginStatus = 'not_started' | 'success' | 'pending' | 'failed' | 'verifying';

function normalizeAddress(address: Address | undefined): Address {
  return {
    line1: address?.line1?.trim() || '',
    line2: address?.line2?.trim() || '',
    line3: address?.line3?.trim() || '',
    city: address?.city?.trim() || '',
    state: address?.state?.trim() || '',
    postalCode: address?.postalCode?.trim() || '',
    country: address?.country || 'US',
  };
}

function parseNameParts(name: string | null | undefined) {
  const [firstName = '', ...remainingNameParts] = name?.trim().split(/\s+/).filter(Boolean) ?? [];
  const lastName = remainingNameParts.pop() ?? '';
  return {
    firstName,
    middleName: remainingNameParts.join(' '),
    lastName,
  };
}

function toDayjsDate(date: Date | string | null | undefined) {
  if (date instanceof Date) {
    return dayjs(date.toISOString().split('T')[0]);
  }

  const normalizedDateOfBirth = normalizeDateOfBirth(date);
  return normalizedDateOfBirth ? dayjs(normalizedDateOfBirth) : undefined;
}

function parseTypedDateOfBirth(value: string) {
  const parsedDateOfBirth = dayjs(value.trim(), DATE_OF_BIRTH_COMMIT_FORMATS, true);
  return parsedDateOfBirth.isValid() ? parsedDateOfBirth : undefined;
}

function buildKycPersonalInfo(values: PersonalInfoFormValues, email: string): KycPersonalInfo {
  const dateOfBirth = values.dateOfBirth.format('YYYY-MM-DD');
  const phoneNumber = normalizeUSPhoneNumber(values.phoneNumber) ?? values.phoneNumber.trim();
  const personalAddress = normalizeAddress(values.personalAddress);
  const street = [personalAddress.line1, personalAddress.line2].filter(Boolean).join(' ');

  return {
    first_name: values.firstName.trim(),
    middle_name: values.middleName?.trim() || undefined,
    last_name: values.lastName.trim(),
    email: email.trim(),
    date_of_birth: dateOfBirth,
    phone_number: phoneNumber,
    street,
    city: personalAddress.city,
    state: personalAddress.state,
    zip_code: personalAddress.postalCode,
    suffix: values.suffix,
    personalAddress,
    dateOfBirthForStore: new Date(`${dateOfBirth}T00:00:00.000Z`),
  };
}

function buildFullLegalName(values: KycPersonalInfo): string {
  return [values.first_name, values.middle_name, values.last_name].filter(Boolean).join(' ');
}

function isPlaidRedirectPending() {
  const urlParams = new URLSearchParams(window.location.search);
  return (
    urlParams.has('plaid_success') ||
    urlParams.has('plaid_error') ||
    urlParams.has('plaid_error_code') ||
    urlParams.has('public_token') ||
    sessionStorage.getItem('plaid_redirect_pending') === 'true'
  );
}

export default function PlaidIntegration() {
  const user = useAppSelector((state) => state.userinfo.user);
  const apexUserInfo = useAppSelector(selectApexUserInfo);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm<PersonalInfoFormValues>();
  const [loginStatus, setLoginStatus] = useState<LoginStatus>(() =>
    isPlaidRedirectPending() ? 'verifying' : 'not_started',
  );
  const [isCompletingPlaidRedirect, setIsCompletingPlaidRedirect] = useState(() =>
    isPlaidRedirectPending(),
  );
  const prefillIdentityKeyRef = useRef<string>();
  const formDisabled = loginStatus === 'pending' || loginStatus === 'verifying';
  const isVerifyingPlaidRedirect = loginStatus === 'verifying';

  const commitTypedDateOfBirth = useCallback(
    (value: string) => {
      const parsedDateOfBirth = parseTypedDateOfBirth(value);
      if (!parsedDateOfBirth) {
        return;
      }

      form.setFieldsValue({ dateOfBirth: parsedDateOfBirth });
      form.validateFields(['dateOfBirth']).catch(() => undefined);
    },
    [form],
  );

  const loadKycResult = useCallback(async () => {
    try {
      const result = await getVerificationResult();
      if (result) {
        dispatch(setKycResult(mapVerificationResultToKycResult(result)));
      }
    } catch (error) {
      console.warn('Unable to load KYC result after Plaid verification:', error);
    }
  }, [dispatch]);

  useEffect(() => {
    const prefillIdentityKey = user?.userId || user?.cognito_id || user?.id || user?.email || '';
    const shouldReplaceTouchedFields = prefillIdentityKeyRef.current !== prefillIdentityKey;
    prefillIdentityKeyRef.current = prefillIdentityKey;
    const nameParts = parseNameParts(user?.name);
    const dateOfBirth = toDayjsDate(apexUserInfo.dateOfBirth ?? user?.date_of_birth);
    const personalAddress = normalizeAddress(apexUserInfo.personalAddress);

    const setPrefillField = (name: string | string[], value: unknown) => {
      if (shouldReplaceTouchedFields || !form.isFieldTouched(name)) {
        form.setFieldValue(name, value);
      }
    };

    setPrefillField('firstName', apexUserInfo.firstName || nameParts.firstName);
    setPrefillField('middleName', apexUserInfo.middleName || nameParts.middleName);
    setPrefillField('lastName', apexUserInfo.lastName || nameParts.lastName);
    setPrefillField('suffix', apexUserInfo.suffix || 'NAME_SUFFIX_UNSPECIFIED');
    setPrefillField('phoneNumber', apexUserInfo.phoneNumber || user?.phone_number || '');
    setPrefillField('dateOfBirth', dateOfBirth?.isValid() ? dateOfBirth : undefined);
    setPrefillField(['personalAddress', 'line1'], personalAddress.line1);
    setPrefillField(['personalAddress', 'line2'], personalAddress.line2);
    setPrefillField(['personalAddress', 'line3'], personalAddress.line3);
    setPrefillField(['personalAddress', 'city'], personalAddress.city);
    setPrefillField(['personalAddress', 'state'], personalAddress.state);
    setPrefillField(['personalAddress', 'postalCode'], personalAddress.postalCode);
    setPrefillField(['personalAddress', 'country'], personalAddress.country);
  }, [apexUserInfo, form, user]);

  const performVerification = useCallback(async () => {
    try {
      setLoginStatus('verifying');

      const response = await getVerificationStatus();
      const isVerified = response?.status === 'success';

      if (isVerified) {
        await loadKycResult();
        setLoginStatus('success');
        dispatch(setVirifiedStatus(true));
        return true;
      } else {
        setLoginStatus('failed');
        dispatch(setVirifiedStatus(false));
        return false;
      }
    } catch (error) {
      console.error('Verification failed:', error);
      setLoginStatus('failed');
      dispatch(setVirifiedStatus(false));
      return false;
    }
  }, [dispatch, loadKycResult]);

  // check if the user is from plaid redirect
  useEffect(() => {
    const checkPlaidRedirect = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const isFromPlaid = isPlaidRedirectPending();

      if (isFromPlaid) {
        setIsCompletingPlaidRedirect(true);

        // clear the url parameters
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);

        // clear the sessionStorage
        sessionStorage.removeItem('plaid_redirect_pending');

        if (urlParams.has('plaid_error') || urlParams.has('plaid_error_code')) {
          setLoginStatus('failed');
          setIsCompletingPlaidRedirect(false);
          dispatch(setVirifiedStatus(false));
          navigate('/kyc/failed', { replace: true });
          return;
        }

        // start the verification process
        setLoginStatus('verifying');
        const isVerified = await performVerification();
        navigate(isVerified ? ACCOUNT_OPENING_NEXT_PATH : '/kyc/failed', { replace: true });
      }
    };

    checkPlaidRedirect();
  }, [dispatch, navigate, performVerification]);

  const startLogIn = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) {
      return;
    }

    if (!user) {
      message.error('Please sign in before starting identity verification.');
      return;
    }

    try {
      setLoginStatus('pending');

      const personalInfo = buildKycPersonalInfo(values, user.email);
      const fullLegalName = buildFullLegalName(personalInfo);
      const addressError = validateAddress(personalInfo.personalAddress);
      if (addressError) {
        message.error(addressError);
        setLoginStatus('failed');
        return;
      }

      const updatedUser = {
        ...user,
        name: fullLegalName,
        phone_number: personalInfo.phone_number,
        date_of_birth: personalInfo.date_of_birth,
      };
      const profileUpdate = await updateCustomerProfile(
        user.cognito_id,
        {
          name: fullLegalName,
          date_of_birth: personalInfo.date_of_birth,
        },
        { showSuccessMessage: false },
      );

      if (!profileUpdate.success) {
        message.error(profileUpdate.message || 'Unable to save your personal information.');
        setLoginStatus('failed');
        return;
      }

      setItemWithExpiry('user', JSON.stringify(updatedUser), AUTHENTICATED_USER_CACHE_TTL_MS);
      dispatch(setUser(updatedUser));
      dispatch(
        updateUserInfo({
          firstName: personalInfo.first_name,
          middleName: personalInfo.middle_name,
          lastName: personalInfo.last_name,
          suffix: personalInfo.suffix,
          phoneNumber: personalInfo.phone_number,
          dateOfBirth: personalInfo.dateOfBirthForStore,
          personalAddress: personalInfo.personalAddress,
        }),
      );

      sessionStorage.setItem('plaid_redirect_pending', 'true');

      // login with plaid
      const success = await loginPlaid(updatedUser, {
        first_name: personalInfo.first_name,
        last_name: personalInfo.last_name,
        email: personalInfo.email,
        date_of_birth: personalInfo.date_of_birth,
        phone_number: personalInfo.phone_number,
        street: personalInfo.street,
        city: personalInfo.city,
        state: personalInfo.state,
        zip_code: personalInfo.zip_code,
      });
      // note: since this is a page redirect, the success is usually true, and the actual verification is done after the redirect
      if (success) {
        // the page will redirect, so this will not be executed
        setLoginStatus('verifying');
      } else {
        setLoginStatus('failed');
        sessionStorage.removeItem('plaid_redirect_pending');
      }
    } catch (error) {
      console.error('Login failed:', error);
      setLoginStatus('failed');
      sessionStorage.removeItem('plaid_redirect_pending');
    }
  };

  const next = async () => {
    try {
      const response = await getVerificationStatus();
      const isVerified = response?.status === 'success';
      dispatch(setVirifiedStatus(isVerified));
      if (isVerified) {
        await loadKycResult();
        navigate(ACCOUNT_OPENING_NEXT_PATH);
      } else {
        navigate('/kyc/failed');
      }
    } catch (error) {
      console.error('Failed to fetch verification status:', error);
      dispatch(setVirifiedStatus(false));
      message.error('Unable to confirm verification status. Please try again.');
    }
  };

  const getButtonText = () => {
    switch (loginStatus) {
      case 'not_started':
        return 'Continue to ID check';
      case 'pending':
        return 'Redirecting to Plaid...';
      case 'verifying':
        return 'Finishing...';
      case 'success':
        return 'Done';
      case 'failed':
        return 'Failed to verify. Retry?';
      default:
        return 'Continue to ID check';
    }
  };

  const handleButtonClick = () => {
    switch (loginStatus) {
      case 'not_started':
      case 'failed':
        startLogIn();
        break;
      case 'success':
        next();
        break;
      case 'pending':
      case 'verifying':
        // do nothing
        break;
    }
  };

  if (isCompletingPlaidRedirect) {
    return <Form form={form} component={false} />;
  }

  const busy = loginStatus === 'pending' || loginStatus === 'verifying';

  return (
    <OpenAccountShell
      step={1}
      cardLabel="Personal information"
      title="Let's confirm who you are"
      intro="Use your legal name and home address. We're required to verify your identity before opening an account."
      footer={
        <>
          <Button type="default" onClick={() => navigate(-1)}>
            Back
          </Button>
          <Button type="primary" loading={busy} onClick={handleButtonClick} disabled={busy}>
            {getButtonText()}
          </Button>
        </>
      }
    >
      {isVerifyingPlaidRedirect ? (
        <Form form={form} component={false} />
      ) : (
        <Form
          className="kyc-personal-info-form"
          form={form}
          layout="vertical"
          requiredMark={false}
          disabled={formDisabled}
        >
          <div className="kyc-grid kyc-name-grid">
            <Form.Item
              label="First name"
              name="firstName"
              rules={[
                { required: true, whitespace: true, message: 'Please enter your first name' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const error = validateName(value);
                    return error ? Promise.reject(new Error(error)) : Promise.resolve();
                  },
                },
              ]}
            >
              <Input autoComplete="given-name" />
            </Form.Item>

            <Form.Item
              label="Middle name"
              name="middleName"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const error = validateMiddleNames(value.split(' ').filter(Boolean));
                    return error ? Promise.reject(new Error(error)) : Promise.resolve();
                  },
                },
              ]}
            >
              <Input autoComplete="additional-name" />
            </Form.Item>

            <Form.Item
              label="Last name"
              name="lastName"
              rules={[
                { required: true, whitespace: true, message: 'Please enter your last name' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const error = validateName(value);
                    return error ? Promise.reject(new Error(error)) : Promise.resolve();
                  },
                },
              ]}
            >
              <Input autoComplete="family-name" />
            </Form.Item>

            <Form.Item
              label="Suffix"
              name="suffix"
              initialValue="NAME_SUFFIX_UNSPECIFIED"
              rules={[{ required: true, message: 'Please select your suffix' }]}
            >
              <Select placeholder="Select suffix">
                {SUFFIX_OPTIONS.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="kyc-grid kyc-contact-grid">
            <Form.Item
              label="Phone number"
              name="phoneNumber"
              rules={[
                { required: true, whitespace: true, message: 'Please enter your phone number' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const error = validateUSPhoneNumber(value);
                    return error ? Promise.reject(new Error(error)) : Promise.resolve();
                  },
                },
              ]}
            >
              <Input autoComplete="tel" inputMode="tel" placeholder="(555) 555-0123" />
            </Form.Item>

            <Form.Item
              label="Date of Birth"
              name="dateOfBirth"
              rules={[
                { required: true, message: 'Please select your date of birth' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const error = validateDateOfBirth(value.toDate());
                    return error ? Promise.reject(new Error(error)) : Promise.resolve();
                  },
                },
              ]}
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder="MM / DD / YYYY"
                format={DATE_OF_BIRTH_PICKER_FORMATS}
                defaultPickerValue={DEFAULT_DATE_OF_BIRTH_PICKER_VALUE}
                showToday={false}
                onBlur={(event) => commitTypedDateOfBirth(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    commitTypedDateOfBirth(event.currentTarget.value);
                  }
                }}
                disabledDate={(current) => current && current > dayjs().endOf('day')}
              />
            </Form.Item>
          </div>

          <div className="open-account-divider" />

          <div className="kyc-grid kyc-address-grid">
            <Form.Item
              className="kyc-street"
              label="Street address"
              name={['personalAddress', 'line1']}
              rules={[{ required: true, whitespace: true, message: 'Please enter your address' }]}
            >
              <Input autoComplete="address-line1" />
            </Form.Item>

            <Form.Item
              className="kyc-unit"
              label="Apartment, suite, etc."
              name={['personalAddress', 'line2']}
            >
              <Input autoComplete="address-line2" />
            </Form.Item>

            <Form.Item
              className="kyc-city"
              label="City"
              name={['personalAddress', 'city']}
              rules={[{ required: true, whitespace: true, message: 'Please enter your city' }]}
            >
              <Input autoComplete="address-level2" />
            </Form.Item>

            <Form.Item
              className="kyc-state"
              label="State"
              name={['personalAddress', 'state']}
              rules={[{ required: true, message: 'Please enter your state' }]}
            >
              <Select
                placeholder="Select your state"
                showSearch
                options={US_STATES.map((state) => ({
                  value: state,
                  label: state,
                }))}
              />
            </Form.Item>

            <Form.Item
              className="kyc-zip"
              label="ZIP code"
              name={['personalAddress', 'postalCode']}
              rules={[{ required: true, whitespace: true, message: 'Please enter your ZIP code' }]}
            >
              <Input autoComplete="postal-code" inputMode="numeric" />
            </Form.Item>
          </div>
        </Form>
      )}

      <p className="open-account-note">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <circle cx="9" cy="12" r="2.5" />
          <path d="M14 10h4M14 14h4" />
        </svg>
        Next, our partner Plaid will verify your identity. You'll come back here when you're done.
      </p>
    </OpenAccountShell>
  );
}
